import DateTimePickerModal, {
  DateTimeResult,
} from "@/src/components/action-modals/date-time-modal";
import InviteUserModal from "@/src/components/action-modals/invite-user-modal";
import UserFilterModal from "@/src/components/action-modals/user-filter-modal";
import CreateRoleModal from "@/src/components/members/roles-and-permission/create-role-modal";
import RolesAndPermission from "@/src/components/members/roles-and-permission/roles-and-permission";
import { type IconProps } from "@/src/components/members/stats-card";
import StatsRow from "@/src/components/members/stats-row";
import UserDashboardHeader from "@/src/components/members/user-dashboard-header";
import ScTabs from "@/src/components/ui-kit/tabs";
import ToolbarCreateButton from "@/src/components/ui-kit/toolbar-create-button";
import { DateRange, UserRole, UserStatus } from "@/src/models/user.model";
import { useAllUsersQuery } from "@/src/services/users.blocks.api";
import { useTheme } from "@/src/styles/theme-provider";
import {
  AddCircle,
  Calendar,
  Filter,
  Profile2User,
  SearchNormal,
  User,
  UserOctagon,
  UserTick,
} from "iconsax-react-nativejs";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Pressable,
  ScrollView,
  Text,
  TextInput,
  useWindowDimensions,
  View,
} from "react-native";
import UserListingTable from "../../components/members/all-users/user-listing";

export const USER_ACCESS_TABS = [
  "LABEL_ALL_USERS",
  "LABEL_ROLES_AND_PERMISSION",
] as const;
export type UserAccessTab = (typeof USER_ACCESS_TABS)[number];

type StatItem = Readonly<{
  count: number;
  text: string;
  icon: React.ReactElement<IconProps>;
}>;

const UserAccessManagement = () => {
  const { t } = useTranslation();
  const { themeVars } = useTheme();
  const { width } = useWindowDimensions();
  const isMobile = width < 768;

  const [activeTab, setActiveTab] = useState<UserAccessTab>("LABEL_ALL_USERS");
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [isCreateRoleOpen, setIsCreateRoleOpen] = useState(false);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [userSearchText, setUserSearchText] = useState("");
  const [roleFilter, setRoleFilter] = useState<UserRole[] | null>(null);
  const [statusFilter, setStatusFilter] = useState<UserStatus | null>(null);
  const [dateRange, setDateRange] = useState<DateRange | null>(null);
  const [filteredUserCount, setFilteredUserCount] = useState<number>(0);

  const startDateISO = dateRange?.start
    ? new Date(dateRange.start).toISOString()
    : undefined;

  const { data: usersData } = useAllUsersQuery(
    userSearchText,
    startDateISO,
    dateRange?.end,
    true,
  );

  const users = usersData?.users ?? [];
  const totalUsers = usersData?.totalCount ?? 0;
  const activeUsers = users.filter((user) => user?.active).length;
  const totalAdmins = users.filter(
    (user) => user?.memberships?.[0]?.roles?.[0]?.toLowerCase() === "admin",
  ).length;
  const totalMembers = users.filter(
    (user) => user?.memberships?.[0]?.roles?.[0]?.toLowerCase() === "member",
  ).length;

  const activeFiltersCount = (roleFilter?.length ?? 0) + (statusFilter ? 1 : 0);

  const stats: readonly StatItem[] = [
    {
      count: activeUsers,
      text: "LABEL_ACTIVE_USERS",
      icon: (
        <Profile2User color={themeVars["--color-chalk-default"] as string} />
      ),
    },
    {
      count: totalUsers,
      text: "LABEL_TOTAL_USERS",
      icon: <UserTick color={themeVars["--color-chalk-default"] as string} />,
    },
    {
      count: totalAdmins,
      text: "LABEL_TOTAL_ADMIN",
      icon: (
        <UserOctagon color={themeVars["--color-chalk-default"] as string} />
      ),
    },
    {
      count: totalMembers,
      text: "LABEL_TOTAL_MEMBERS",
      icon: <User color={themeVars["--color-chalk-default"] as string} />,
    },
  ];

  const dateRangeLabel = dateRange
    ? `${dateRange.start} - ${dateRange.end}`
    : t("LABEL_SELECT_DATE");

  const handleDateRangeApply = (result: DateTimeResult) => {
    if (!result.startDate || !result.endDate) {
      setDateRange(null);
      return;
    }
    setDateRange({ start: result.startDate, end: result.endDate });
  };

  const handleFilterApply = (filters: {
    role: UserRole[] | null;
    status: UserStatus | null;
  }) => {
    setRoleFilter(filters.role);
    setStatusFilter(filters.status);
  };

  return (
    <View className="flex-row flex-1 bg-obsidian">
      <ScrollView
        className="flex-1"
        showsVerticalScrollIndicator={false}
        contentContainerClassName="grow"
      >
        <View className="flex-1 flex-col rounded-2xl gap-2">
          <UserDashboardHeader />

          <StatsRow stats={stats} />

          <View className="justify-start gap-4 md:min-h-[60%] md:bg-chalk md:rounded-2xl md:border md:border-ash-3 md:p-6">
            {/* Tabs + (on the Roles tab) the Create-role action on the right */}
            <View className="flex-row items-center justify-between gap-4">
              <ScTabs
                tabs={USER_ACCESS_TABS}
                active={activeTab}
                onChange={setActiveTab}
              />
              {activeTab === "LABEL_ROLES_AND_PERMISSION" && (
                <ToolbarCreateButton
                  text="LABEL_CREATE_ROLE"
                  onPress={() => setIsCreateRoleOpen(true)}
                  className="h-12 md:h-11"
                />
              )}
            </View>

            {/* Search + Date + Filter + Invite row */}
            {activeTab !== "LABEL_ROLES_AND_PERMISSION" && (
              <View className="flex-row justify-between items-center gap-4 flex-wrap">
                {/* Search */}
                <View className="flex-row flex-[3] min-w-[120px] items-center gap-2 bg-chalk md:bg-ash rounded-2xl px-4 h-12 md:h-11 md:border md:border-ash-3">
                  <SearchNormal
                    size={20}
                    className="shrink-0"
                    color={themeVars["--color-slate-default"] as string}
                  />
                  <TextInput
                    placeholder={t("LABEL_SEARCH_USERS")}
                    placeholderTextColor={
                      themeVars["--color-slate-default"] as string
                    }
                    className="flex-1 min-w-[80px] text-onyx focus:outline-none overflow-ellipsis"
                    value={userSearchText}
                    onChangeText={setUserSearchText}
                  />
                  {isMobile && (
                    <Pressable
                      onPress={() => setIsInviteModalOpen(true)}
                      hitSlop={8}
                      className="active:opacity-70 shrink-0 hover:rounded-full hover:bg-ash-3"
                    >
                      <AddCircle
                        size={24}
                        color={themeVars["--color-primary-default"] as string}
                      />
                    </Pressable>
                  )}
                </View>

                {/* Date picker */}
                <Pressable
                  onPress={() =>
                    setIsDatePickerOpen((previousOpen) => !previousOpen)
                  }
                  className="flex-row md:flex-1 md:min-w-[150px] md:max-w-[230px] justify-center md:justify-evenly bg-chalk md:bg-ash gap-2 md:gap-3 items-center rounded-2xl px-4 h-12 md:h-11 md:border md:border-ash-3 hover:bg-ash-3"
                >
                  <Calendar
                    size={20}
                    color={themeVars["--color-primary-default"] as string}
                  />
                  <Text className="text-slate hidden xl:block">
                    {dateRangeLabel}
                  </Text>
                </Pressable>

                {/* Filter */}
                <View className="flex-row bg-chalk md:bg-ash justify-center items-center gap-3 rounded-2xl px-4 h-12 md:h-11 relative overflow-hidden md:border md:border-ash-3">
                  <Pressable
                    onPress={() => setIsFilterOpen(true)}
                    className="flex-row items-center gap-2 py-1 xl:pr-2 border-0 border-ash-3 xl:border-r hover:bg-ash-3 hover:rounded-lg"
                  >
                    <Filter
                      size={20}
                      color={themeVars["--color-primary-default"] as string}
                    />
                    <Text className="text-slate hidden xl:flex">
                      {t("LABEL_FILTER")}
                    </Text>
                    {activeFiltersCount > 0 && (
                      <View className="flex-row justify-center bg-primary items-center rounded-full px-2">
                        <Text className="text-chalk text-xs">
                          {activeFiltersCount}
                        </Text>
                      </View>
                    )}
                  </Pressable>
                  <View className="hidden xl:flex flex-row items-center gap-1 pl-2">
                    <Text className="text-slate text-sm">
                      {filteredUserCount} {t("LABEL_USERS")}
                    </Text>
                  </View>
                </View>

                {/* Invite button — fixed height to line up with the
                    search / date / filter controls (md:h-12). */}
                <ToolbarCreateButton
                  text="BTN_INVITE"
                  mode="contained"
                  icon={<AddCircle />}
                  disabled={true}
                  onPress={() => setIsInviteModalOpen(true)}
                  hideOnMobile
                  className="h-12 md:h-11"
                />
              </View>
            )}

            {activeTab === "LABEL_ALL_USERS" && (
              <UserListingTable
                search={userSearchText}
                startDate={startDateISO}
                endDate={dateRange?.end}
                roleFilter={roleFilter}
                statusFilter={statusFilter}
                onFilteredCountChange={setFilteredUserCount}
              />
            )}

            {activeTab === "LABEL_ROLES_AND_PERMISSION" && (
              <RolesAndPermission />
            )}
          </View>
        </View>
      </ScrollView>

      <InviteUserModal
        visible={isInviteModalOpen}
        onClose={() => setIsInviteModalOpen(false)}
      />
      <DateTimePickerModal
        variant="date-only"
        visible={isDatePickerOpen}
        onClose={() => setIsDatePickerOpen(false)}
        onApply={handleDateRangeApply}
      />

      <UserFilterModal
        visible={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        hideStatus={false}
        onApply={handleFilterApply}
      />

      <CreateRoleModal
        visible={isCreateRoleOpen}
        onClose={() => setIsCreateRoleOpen(false)}
      />
    </View>
  );
};

export default UserAccessManagement;
